import { useEffect, useMemo, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { AppShell } from "@/components/shell/AppShell";
import { ModuleHeader, FeedChips, Toolbar, DateRangePills, Segmented, SectionTabs, EmptyState, Loading } from "@/components/kit";
import { useTenant } from "@/context/TenantContext";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { filterData, kpisOf, presetsFor, ACCENT } from "./ivUtils";
import IvExecView from "./IvExecView";
import IvTlAgentsView from "./IvTlAgentsView";
import IvQualityView from "./IvQualityView";
import IvAgentDrawer from "./IvAgentDrawer";

const VIEWS = [
  { id: "exec", label: "Exec Summary" },
  { id: "tl", label: "TL & Agents" },
  { id: "quality", label: "Quality Deep-Dive" },
];
const SITES = ["All", "Qatar", "India", "Unknown"];

export default function InboundVoicePage() {
  const { tenantId, tenant } = useTenant() || {};
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [view, setView] = useState(params.get("view") || "exec");
  const [site, setSite] = useState(params.get("site") || "All");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [drawer, setDrawer] = useState(params.get("agent") || null);

  const load = useCallback((refresh = false) => {
    if (!tenantId) { setError("No client selected. Use the client switcher in the header."); return; }
    setBusy(true); setError(null);
    api.get(`/modules/inbound-voice/dashboard?tenant_id=${tenantId}&refresh=${refresh}`)
      .then((r) => {
        setData(r.data);
        const p = presetsFor(r.data);
        setFrom((f) => f || `${p.month}-01`);
        setTo((t) => t || p.latest);
      })
      .catch((e) => setError(apiError(e)))
      .finally(() => setBusy(false));
  }, [tenantId]);
  useEffect(() => { setData(null); load(); }, [load]);

  useEffect(() => {
    const next = {};
    if (view !== "exec") next.view = view;
    if (site !== "All") next.site = site;
    if (drawer) next.agent = drawer;
    setParams(next, { replace: true });
  }, [view, site, drawer, setParams]);

  const presets = useMemo(() => (data ? presetsFor(data) : null), [data]);
  const slices = useMemo(() => (data && from && to ? filterData(data, { from, to, site }) : null), [data, from, to, site]);
  const kpis = useMemo(() => (slices ? kpisOf(slices) : null), [slices]);
  const isAdmin = ["admin", "super_admin"].includes(user?.role);

  return (
    <AppShell>
      <ModuleHeader testid="iv-title" title="Inbound Voice" subtitle="IB 111 Consumer" accent={ACCENT}
        context={`${tenant?.name || "No client"} · Workspace data · monthly uploads`}
        meta={data ? `Snapshot · ${(data.months_loaded || []).length} months loaded` : null} onRefresh={data ? () => load(true) : null} busy={busy} />

      {error ? (
        <EmptyState testid="iv-error" title="No data for this module" body={error} isAdmin={isAdmin} action={{ to: "/settings?section=uploads", label: "Upload monthly data" }} />
      ) : !data || !slices ? <Loading /> : (
        <>
          <FeedChips testid="iv-coverage-banner" coverage={data.coverage} months={data.months_loaded || []} hint="A missing month is a coverage gap, not zero performance" />
          <Toolbar testid="iv-toolbar">
            <DateRangePills prefix="iv" from={from} to={to} setFrom={setFrom} setTo={setTo} presets={presets.list} />
            <Segmented testidPrefix="iv-site" options={SITES} value={site} onChange={setSite} className="ml-auto" />
          </Toolbar>
          <SectionTabs testidPrefix="iv-view" tabs={VIEWS} value={view} onChange={setView} accent={ACCENT} />

          {view === "exec" && <IvExecView data={data} slices={slices} kpis={kpis} range={{ from, to }} />}
          {view === "tl" && <IvTlAgentsView data={data} slices={slices} site={site} onOpenAgent={setDrawer} />}
          {view === "quality" && <IvQualityView data={data} slices={slices} onOpenAgent={setDrawer} />}
          {drawer && <IvAgentDrawer data={data} cisco={drawer} onClose={() => setDrawer(null)} />}
        </>
      )}
    </AppShell>
  );
}
